import { motion } from "framer-motion";
import { ArrowLeft, Home, X, Plus, Minus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const EditRegistrationPage = ({ sharedFormData, isEditMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Form data state - UPDATED TO MATCH NEW STRUCTURE
  const [formData, setFormData] = useState({
    name: "",
    batch: "",
    mobile: "",
    email: "",
    tshirtSize: "",
    accompany: {
      spouse: 0,
      guest: 0,
      driver: 0,
      childAbove5: 0,
      childBelow5: 0,
    },
    childAbove5TshirtSize: "",
    childBelow5TshirtSize: "",
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [batchPricing, setBatchPricing] = useState({});
  const [tShirtSize, setTShirtSize] = useState([]);
  const [childAbove5TShirtSize, setChildAbove5TShirtSize] = useState([]);
  const [childBelow5TShirtSize, setChildBelow5TShirtSize] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [registration, setRegistration] = useState(null);
  const [gender, setGender] = useState("Male");
  const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
    useState(false);
  const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
    useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  const { user, getAuthHeader } = useAuth();

  const stringToArray = (str) => {
    if (!str) return [];
    return str
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);
  };

  const arrayToString = (arr) => {
    if (!arr || arr.length === 0) return "";
    return arr.join(",");
  };

  useEffect(() => {
    if (isEditMode && id) {
      const fetchRegistrationById = async () => {
        try {
          setLoading(true);
          const data = await api.registration.getById(id);
          setRegistration(data);

          // UPDATED FOR NEW STRUCTURE
          setFormData({
            name: data.name || "",
            batch: data.batch || "",
            mobile: data.mobile || "",
            email: data.email || "",
            tshirtSize: data.tshirtSize || "",
            accompany: {
              spouse: data.accompany?.spouse || 0,
              guest: data.accompany?.guest || 0,
              driver: data.accompany?.driver || 0,
              childAbove5: data.accompany?.childAbove5 || 0,
              childBelow5: data.accompany?.childBelow5 || 0,
            },
            childAbove5TshirtSize: data.childAbove5TshirtSize || "",
            childBelow5TshirtSize: data.childBelow5TshirtSize || "",
          });

          if (data.gender) {
            setGender(data.gender || "Male");
          }
        } catch (error) {
          console.error("❌ Error fetching registration:", error);
          alert("Failed to load registration data.");
          // navigate("/auth/admin");
          navigate("/admin");

        } finally {
          setLoading(false);
        }
      };

      fetchRegistrationById();
    }
  }, [isEditMode, id, navigate]);

  useEffect(() => {
    if (!isEditMode && sharedFormData) {
      setFormData((prev) => ({
        ...prev,
        name: sharedFormData.name || "",
        batch: sharedFormData.batch || "",
        mobile: sharedFormData.mobile || "",
        email: sharedFormData.email || "",
      }));
    }
  }, [sharedFormData, isEditMode]);

  useEffect(() => {
    const fetchBatchPricing = async () => {
      try {
        const data = await api.batchPricing.getAll();
        const pricingMap = {};
        data.forEach((bp) => {
          pricingMap[bp.batchName] = {
            participantFee: bp.participantFee,
            spouseFee: bp.spouseFee || 0,
            guestFee: bp.guestFee || 0,
            driverFee: bp.driverFee || 0,
            above5Fee: bp.above5Fee || 0,
            below5Fee: bp.below5Fee || 0,
          };
        });
        setBatchPricing(pricingMap);
      } catch (err) {
        console.error("Error fetching batch pricing:", err);
      }
    };

    fetchBatchPricing();
  }, []);

  useEffect(() => {
    if (!gender) return;

    const fetchTShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender(gender);
        const tShirtSizes = data.map((item) => item.name);
        setTShirtSize(tShirtSizes);
      } catch (err) {
        console.error("Error fetching t-shirt:", err);
      }
    };

    fetchTShirtSizes();
  }, [gender]);

  // Fetch T-shirt sizes for children above 5
  useEffect(() => {
    const fetchChildAbove5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child>5");
        const tShirtSizes = data.map((item) => item.name);
        setChildAbove5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error("Error fetching t-shirt sizes for children above 5:", err);
      }
    };

    fetchChildAbove5TShirtSizes();
  }, []);

  // Fetch T-shirt sizes for children below 5
  useEffect(() => {
    const fetchChildBelow5TShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("Child<5");
        const tShirtSizes = data.map((item) => item.name);
        setChildBelow5TShirtSize(tShirtSizes);
      } catch (err) {
        console.error("Error fetching t-shirt sizes for children below 5:", err);
      }
    };

    fetchChildBelow5TShirtSizes();
  }, []);

  const handleHomeClick = () => {
    navigate("/home");
  };

  const calculateTotal = () => {
    const batch = batchPricing[formData.batch];
    if (!batch) return 0;

    return (
      batch.participantFee +
      formData.accompany.spouse * (batch.spouseFee || 0) +
      formData.accompany.guest * (batch.guestFee || 0) +
      formData.accompany.driver * (batch.driverFee || 0) +
      formData.accompany.childAbove5 * (batch.above5Fee || 0) +
      formData.accompany.childBelow5 * (batch.below5Fee || 0)
    );
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleAccompanyChange = (type, inc) => {
    const newCount = Math.max(0, formData.accompany[type] + inc);

    setFormData((prev) => {
      // Only child categories have t-shirt size options
      let sizeField = "";
      if (type === "childAbove5") {
        sizeField = "childAbove5TshirtSize";
      } else if (type === "childBelow5") {
        sizeField = "childBelow5TshirtSize";
      }

      if (sizeField) {
        const currentSizes = stringToArray(prev[sizeField]);
        let updatedSizes = currentSizes;

        if (newCount < prev.accompany[type]) {
          // Decreasing count - remove last size
          updatedSizes = currentSizes.slice(0, newCount);
        } else if (newCount === 0) {
          updatedSizes = [];
        }

        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
          [sizeField]: arrayToString(updatedSizes),
        };
      } else {
        // For spouse, guest, driver - no t-shirt size selection needed
        return {
          ...prev,
          accompany: {
            ...prev.accompany,
            [type]: newCount,
          },
        };
      }
    });
  };

  const handleTShirtSizeModalSelect = (type, size, action) => {
    const currentSizes = stringToArray(formData[type]);
    const maxSelections =
      formData.accompany[
        type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
      ];

    // Count occurrences of the selected size
    const sizeCount = currentSizes.filter((s) => s === size).length;

    let newSizes = [...currentSizes];

    if (action === 'increase') {
      // Increase count if we haven't reached max selections
      if (currentSizes.length < maxSelections) {
        newSizes.push(size);
      }
    } else if (action === 'decrease') {
      // Decrease count if this size exists
      if (sizeCount > 0) {
        const indexToRemove = newSizes.lastIndexOf(size);
        if (indexToRemove > -1) {
          newSizes.splice(indexToRemove, 1);
        }
      }
    } else if (action === 'remove') {
      // Remove all occurrences of this size
      newSizes = newSizes.filter((s) => s !== size);
    }

    setFormData((prev) => ({
      ...prev,
      [type]: arrayToString(newSizes),
    }));
  };

  const handleClearSelection = (type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.mobile.trim()) newErrors.mobile = "Mobile required";
    else if (!/^\d{11}$/.test(formData.mobile))
      newErrors.mobile = "Must be 11 digits";

    // Validate child above 5 t-shirt sizes
    const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
    if (
      formData.accompany.childAbove5 > 0 &&
      childAbove5Sizes.length !== formData.accompany.childAbove5
    ) {
      newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
    }

    // Validate child below 5 t-shirt sizes
    const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
    if (
      formData.accompany.childBelow5 > 0 &&
      childBelow5Sizes.length !== formData.accompany.childBelow5
    ) {
      newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return false;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("batch", formData.batch);
      formDataToSend.append("mobile", formData.mobile);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("tshirtSize", formData.tshirtSize);
      formDataToSend.append("gender", gender);
      formDataToSend.append("accompanySpouse", formData.accompany.spouse);
      formDataToSend.append("accompanyGuest", formData.accompany.guest);
      formDataToSend.append("accompanyDriver", formData.accompany.driver);
      formDataToSend.append(
        "accompanyChildAbove5",
        formData.accompany.childAbove5
      );
      formDataToSend.append(
        "accompanyChildBelow5",
        formData.accompany.childBelow5
      );
      formDataToSend.append(
        "childAbove5TshirtSize",
        formData.childAbove5TshirtSize || ""
      );
      formDataToSend.append(
        "childBelow5TshirtSize",
        formData.childBelow5TshirtSize || ""
      );

      if (user?.id) {
        formDataToSend.append("userId", user.id.toString());
      }

      let data;
      if (isEditMode && id) {
        // Update existing registration
        data = await api.registration.updateReg(id, formDataToSend);
      } else {
        // Create new registration
        data = await api.registration.create(formDataToSend);
      }

      // Reset form for create mode
      if (!isEditMode) {
        setFormData({
          name: "",
          batch: "",
          mobile: "",
          email: "",
          tshirtSize: "",
          accompany: {
            spouse: 0,
            guest: 0,
            driver: 0,
            childAbove5: 0,
            childBelow5: 0,
          },
          childAbove5TshirtSize: "",
          childBelow5TshirtSize: "",
        });
        setGender("Male");
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      // For edit mode, navigate back to admin after successful update
      if (isEditMode) {
        // setTimeout(() => navigate("/auth/admin"), 1500);
        setTimeout(() => navigate("/admin"), 1500);

      }

      return data;
    } catch (error) {
      console.error("Error submitting registration:", error);
      alert(
        `Registration ${isEditMode ? "update" : "failed"}: ${error.message}`
      );
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (participantData) => {
    if (!validateForm()) return;

    try {
      const payload = {
        name: formData.name,
        batch: formData.batch,
        mobile: formData.mobile,
        email: formData.email,
        tshirtSize: formData.tshirtSize,
        accompanySpouse: formData.accompany.spouse,
        accompanyGuest: formData.accompany.guest,
        accompanyDriver: formData.accompany.driver,
        accompanyChildAbove5: formData.accompany.childAbove5,
        accompanyChildBelow5: formData.accompany.childBelow5,
        totalAmount: calculateTotal(),
        order_id: participantData?.orderId || `order_${Date.now()}`,
        participantId: participantData?.id || id,
        userId: user?.id,
        childAbove5TshirtSize: formData.childAbove5TshirtSize,
        childBelow5TshirtSize: formData.childBelow5TshirtSize,
      };

      const data = await api.payment.create(payload);

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert(data.error || "Failed to get checkout URL");
      }
    } catch (error) {
      console.error("Payment request failed", error);
      alert("Payment request failed. Try again.");
    }
  };

  const handleSave = async () => {
    const submitted = await handleSubmit();
    if (submitted && !isEditMode) {
      // Only proceed to payment for new registrations in create mode
      await handlePayment(submitted);
    }
  };

  const TShirtSizeModal = ({
    isOpen,
    onClose,
    onSelect,
    selectedSizesStr,
    title,
    maxSelections,
    availableSizes,
    onClear,
  }) => {
    if (!isOpen) return null;

    const selectedSizes = stringToArray(selectedSizesStr);

    // Calculate count of each selected size
    const sizeCounts = selectedSizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    const totalSelected = selectedSizes.length;
    const remainingToSelect = maxSelections - totalSelected;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
          <div className="mb-4">
            <p className="text-gray-600">
              Select <span className="font-bold">{maxSelections}</span> - Remaining: <span className="font-bold">{remainingToSelect}</span>
            </p>
          </div>

          {/* Clear button */}
          {selectedSizes.length > 0 && (
            <div className="mb-4 flex justify-end">
              <button
                onClick={onClear}
                className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
              >
                Clear All
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            {availableSizes.map((size) => {
              const selectedCount = sizeCounts[size] || 0;
              const canIncrease = remainingToSelect > 0;
              const canDecrease = selectedCount > 0;

              return (
                <div
                  key={size}
                  className={`p-3 rounded-xl font-medium transition-all ${
                    selectedCount > 0
                      ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
                      : "bg-white text-gray-700 border border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg">{size}</span>
                      {selectedCount > 0 && (
                        <span className="text-white font-bold">
                          × {selectedCount}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Decrease Button */}
                      <button
                        onClick={() => onSelect(size, 'decrease')}
                        disabled={!canDecrease}
                        className={`p-1 rounded-full ${
                          canDecrease
                            ? "bg-red-100 text-red-600 hover:bg-red-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Decrease"
                      >
                        <Minus size={18} />
                      </button>
                      
                      {/* Increase Button */}
                      <button
                        onClick={() => onSelect(size, 'increase')}
                        disabled={!canIncrease}
                        className={`p-1 rounded-full ${
                          canIncrease
                            ? "bg-green-100 text-green-600 hover:bg-green-200"
                            : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                        title="Increase"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-gray-600">
              Selected:{" "}
              <span className="font-bold">{totalSelected}</span>/
              {maxSelections}
            </div>
            {selectedSizes.length > 0 && (
              <div className="text-sm text-gray-700 max-w-[60%]">
                <span className="font-medium">Summary:</span>{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size} (${count})`)
                  .join(", ")}
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSizeButton = (type) => {
    const sizeField =
      type === "childAbove5"
        ? "childAbove5TshirtSize"
        : "childBelow5TshirtSize";
    const sizesStr = formData[sizeField];
    const sizes = stringToArray(sizesStr);
    const count = formData.accompany[type];

    if (count === 0) return null;

    // Calculate count of each size
    const sizeCounts = sizes.reduce((acc, size) => {
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});

    return (
      <div className="ml-3">
        <button
          type="button"
          onClick={() =>
            type === "childAbove5"
              ? setShowChildAbove5TShirtModal(true)
              : setShowChildBelow5TShirtModal(true)
          }
          style={{
            padding: "6px 12px",
            backgroundColor: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: "5px",
            fontSize: "12px",
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
            minWidth: "120px",
            opacity:
              isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
          }}
          disabled={
            isEditMode && registration?.paymentStatus === "Paid"
          }
        >
          {sizes.length === 0 ? (
            `Select ${count} T-Shirt(s)`
          ) : (
            <div>
              <div>
                Sizes:{" "}
                {Object.entries(sizeCounts)
                  .map(([size, count]) => `${size}(${count})`)
                  .join(", ")}
              </div>
              <div style={{ fontSize: "10px", opacity: 0.8 }}>
                {sizes.length} of {count} selected
              </div>
            </div>
          )}
        </button>
      </div>
    );
  };

  // Updated accompany categories with proper labels and pricing
  const accompanyCategories = [
    { key: "spouse", label: "Spouse", showTshirt: false },
    { key: "childAbove5", label: "Children Above 5", showTshirt: true },
    { key: "childBelow5", label: "Children Below 5", showTshirt: true },
    { key: "guest", label: "Guest", showTshirt: false },
    { key: "driver", label: "Driver", showTshirt: false },
  ];

  if (loading && isEditMode) {
    return (
      <div className="form-container px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-white">Loading registration data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container px-4">
      {/* Header */}
      <h2 className="form-title m-0">
        {isEditMode ? "Edit Registration" : "Event Registration"}
      </h2>
      <h5
        style={{ textAlign: "center", color: "#bdc0ec", marginBottom: "20px" }}
      >
        KGC'97 Day 2026
      </h5>

      {showSuccess && (
        <div className="success-message">
          🎉 Registration {isEditMode ? "updated" : "submitted"} successfully!
        </div>
      )}

      {/* Payment Status Warning for Edit Mode */}
      {isEditMode && registration?.paymentStatus === "Paid" && (
        <div className="alert alert-warning mb-4">
          <strong>Note:</strong> This registration has already been paid.
          Editing may affect payment details.
        </div>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        {/* Mobile */}
        <div className="form-group">
          <label className="form-label">Mobile Number*</label>
          <input
            type="tel"
            className="form-input"
            placeholder="01XXXXXXXXX (11 digits)"
            maxLength="11"
            value={formData.mobile}
            onChange={(e) => handleInputChange("mobile", e.target.value)}
            disabled={isEditMode && registration?.paymentStatus === "Paid"}
            readOnly={isEditMode && registration?.paymentStatus === "Paid"}
            style={
              isEditMode && registration?.paymentStatus === "Paid"
                ? { backgroundColor: "#e9ecef", color: "#495057" }
                : {}
            }
          />
          {errors.mobile && (
            <div className="error-message">{errors.mobile}</div>
          )}
        </div>

        {/* Name */}
        <div className="form-group">
          <label className="form-label">Name of Participant *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isEditMode && registration?.paymentStatus === "Paid"}
            readOnly={isEditMode && registration?.paymentStatus === "Paid"}
            style={
              isEditMode && registration?.paymentStatus === "Paid"
                ? { backgroundColor: "#e9ecef", color: "#495057" }
                : {}
            }
          />
          {errors.name && <div className="error-message">{errors.name}</div>}
        </div>

        {/* Batch */}
        <div style={{ width: "100%", position: "relative" }}>
          <label className="form-label">Group</label>
          <select
            value={formData.batch}
            onChange={(e) => handleInputChange("batch", e.target.value)}
            disabled={isEditMode && registration?.paymentStatus === "Paid"}
            style={{
              width: "100%",
              padding: "8px 15px",
              borderRadius: "0px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background:
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#e9ecef"
                  : "rgba(255,255,255,0.1)",
              color:
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#495057"
                  : "white",
              fontSize: "1rem",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              backdropFilter: "blur(8px)",
              transition: "all 0.3s ease",
              backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#495057"
                  : "white"
              }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
              backgroundSize: "1rem",
              marginBottom: "15px",
            }}
          >
            <option
              value=""
              style={{ backgroundColor: "#cffae0", color: "black" }}
            >
              Select your group
            </option>
            {Object.keys(batchPricing).map((batch) => (
              <option
                key={batch}
                value={batch}
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                {batch}
              </option>
            ))}
          </select>
        </div>

        {/* Email */}
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            placeholder="your.email@example.com"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            disabled={isEditMode && registration?.paymentStatus === "Paid"}
            readOnly={isEditMode && registration?.paymentStatus === "Paid"}
            style={
              isEditMode && registration?.paymentStatus === "Paid"
                ? { backgroundColor: "#e9ecef", color: "#495057" }
                : {}
            }
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        {/* Gender Selection */}
        <div className="form-group">
          <label className="form-label">Select Gender</label>
          <div
            style={{
              display: "flex",
              gap: "20px",
              color: "white",
              marginBottom: "15px",
            }}
          >
            <label
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <input
                type="radio"
                name="gender"
                value="Male"
                checked={gender === "Male"}
                onChange={(e) => setGender(e.target.value)}
                disabled={isEditMode && registration?.paymentStatus === "Paid"}
              />
              Male
            </label>
            <label
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <input
                type="radio"
                name="gender"
                value="Female"
                checked={gender === "Female"}
                onChange={(e) => setGender(e.target.value)}
                disabled={isEditMode && registration?.paymentStatus === "Paid"}
              />
              Female
            </label>
          </div>
        </div>

        {/* T-shirt Size */}
        <div style={{ width: "100%", position: "relative" }}>
          <label className="form-label">T-Shirt Size</label>
          <select
            value={formData.tshirtSize}
            onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
            disabled={isEditMode && registration?.paymentStatus === "Paid"}
            style={{
              width: "100%",
              padding: "8px 15px",
              borderRadius: "0px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              background:
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#e9ecef"
                  : "rgba(255,255,255,0.1)",
              color:
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#495057"
                  : "white",
              fontSize: "1rem",
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
              backdropFilter: "blur(8px)",
              transition: "all 0.3s ease",
              backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
                isEditMode && registration?.paymentStatus === "Paid"
                  ? "#495057"
                  : "white"
              }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
              backgroundSize: "1rem",
              marginBottom: "15px",
            }}
          >
            <option
              value=""
              style={{ backgroundColor: "#cffae0", color: "black" }}
            >
              Select your T-Shirt Size
            </option>
            {tShirtSize.map((size) => (
              <option
                key={size}
                value={size}
                style={{ backgroundColor: "#cffae0", color: "black" }}
              >
                {size}
              </option>
            ))}
          </select>
          {errors.tshirtSize && (
            <div className="error-message">{errors.tshirtSize}</div>
          )}
        </div>

       {/* Accompany Section - UPDATED */}
<div className="form-group">
  <label className="form-label">Accompanying Persons</label>
  <div
    className="accompany-section"
    style={{
      padding: "15px",
      borderRadius: "12px",
      background: "rgba(255,255,255,0.2)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      opacity:
        isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
    }}
  >
    {accompanyCategories.map((category) => (
      <div key={category.key}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "15px",
            color: "#fff",
            flexWrap: "wrap",
          }}
        >
          <span style={{ flex: 1 }}>
            {category.label} (
            {(() => {
              const amount =
                batchPricing[formData.batch]?.[
                  category.key === "spouse"
                    ? "spouseFee"
                    : category.key === "guest"
                    ? "guestFee"
                    : category.key === "driver"
                    ? "driverFee"
                    : category.key === "childAbove5"
                    ? "above5Fee"
                    : "below5Fee"
                ] || 0;

              return amount > 0 ? `Tk. ${amount}` : "Free";
            })()}
            )
          </span>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {/* Display the number instead of + and - buttons */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "30px",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "5px",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "bold"
            }}>
              {formData.accompany[category.key]}
            </div>

            {/* Only show T-shirt size button for children categories */}
            {category.showTshirt && renderSizeButton(category.key)}
          </div>
        </div>

        {/* Display validation errors for children T-shirt sizes */}
        {formData.accompany[category.key] > 0 &&
          category.showTshirt && (
            <div style={{ marginBottom: "10px" }}>
              {category.key === "childAbove5" &&
                errors.childAbove5TshirtSize && (
                  <div
                    className="error-message"
                    style={{
                      color: "#ff6b6b",
                      marginTop: "5px",
                      fontSize: "12px",
                    }}
                  >
                    {errors.childAbove5TshirtSize}
                  </div>
                )}
              {category.key === "childBelow5" &&
                errors.childBelow5TshirtSize && (
                  <div
                    className="error-message"
                    style={{
                      color: "#ff6b6b",
                      marginTop: "5px",
                      fontSize: "12px",
                    }}
                  >
                    {errors.childBelow5TshirtSize}
                  </div>
                )}
            </div>
          )}
      </div>
    ))}
  </div>
</div>

        {/* T-Shirt Size Modals */}
        <TShirtSizeModal
          isOpen={showChildAbove5TShirtModal}
          onClose={() => setShowChildAbove5TShirtModal(false)}
          onSelect={(size, action) =>
            handleTShirtSizeModalSelect("childAbove5TshirtSize", size, action)
          }
          selectedSizesStr={formData.childAbove5TshirtSize}
          title="Select T-Shirt Sizes for Children Above 5"
          maxSelections={formData.accompany.childAbove5}
          availableSizes={childAbove5TShirtSize}
          onClear={() => handleClearSelection("childAbove5TshirtSize")}
        />

        <TShirtSizeModal
          isOpen={showChildBelow5TShirtModal}
          onClose={() => setShowChildBelow5TShirtModal(false)}
          onSelect={(size, action) =>
            handleTShirtSizeModalSelect("childBelow5TshirtSize", size, action)
          }
          selectedSizesStr={formData.childBelow5TshirtSize}
          title="Select T-Shirt Sizes for Children Below 5"
          maxSelections={formData.accompany.childBelow5}
          availableSizes={childBelow5TShirtSize}
          onClear={() => handleClearSelection("childBelow5TshirtSize")}
        />

        {/* Total */}
        <div
          className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "1.5rem",
            marginTop: "20px",
          }}
        >
          <span>Total amount:</span>
          <span className="amount-title">৳{calculateTotal()}</span>
        </div>

        {/* Buttons */}
        <div className="button-group flex gap-4 mt-6">
          {!isEditMode ? (
            // For new registration
            <button
              type="button"
              className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={loading || (isEditMode && registration?.paymentStatus === "Success")}
            >
              {loading ? (
                <>
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Processing...
                </>
              ) : (
                "Submit & Pay Now"
              )}
            </button>
          ) : (
            // For edit mode - two buttons
            <>
              

              <button
                type="button"
                className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-blue-600 text-white rounded-xl transition"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Updating...
                  </>
                ) : (
                  "Update Changes"
                )}
              </button>
            </>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            Back
          </button>

          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
            onClick={handleHomeClick}
          >
            <Home size={18} />
            Home
          </button>
        </div>

        <div className="d-flex justify-content-center mt-4">
          <img
            src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
            alt="Payment"
            className="img-fluid h-25 object-fit-cover rounded-2"
          />
        </div>

        <div className="hero-text mt-8 flex justify-center items-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ fontSize: "16px", color: "#fff" }}
          >
            পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
          </motion.p>
        </div>
      </form>
    </div>
  );
};

export default EditRegistrationPage;






// import { motion } from "framer-motion";
// import { ArrowLeft, Home, X } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const EditRegistrationPage = ({ sharedFormData, isEditMode = false }) => {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   // Form data state - UPDATED TO MATCH NEW STRUCTURE
//   const [formData, setFormData] = useState({
//     name: "",
//     batch: "",
//     mobile: "",
//     email: "",
//     tshirtSize: "",
//     accompany: {
//       spouse: 0,
//       guest: 0,
//       driver: 0,
//       childAbove5: 0,
//       childBelow5: 0,
//     },
//     childAbove5TshirtSize: "",
//     childBelow5TshirtSize: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [batchPricing, setBatchPricing] = useState({});
//   const [tShirtSize, setTShirtSize] = useState([]);
//   const [loading, setLoading] = useState(isEditMode);
//   const [registration, setRegistration] = useState(null);
//   const [gender, setGender] = useState("Male");
//   const [showChildAbove5TShirtModal, setShowChildAbove5TShirtModal] =
//     useState(false);
//   const [showChildBelow5TShirtModal, setShowChildBelow5TShirtModal] =
//     useState(false);

//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const { user, getAuthHeader } = useAuth();

//   const stringToArray = (str) => {
//     if (!str) return [];
//     return str
//       .split(",")
//       .map((s) => s.trim())
//       .filter((s) => s);
//   };

//   const arrayToString = (arr) => {
//     if (!arr || arr.length === 0) return "";
//     return arr.join(",");
//   };

//   useEffect(() => {
//     if (isEditMode && id) {
//       const fetchRegistrationById = async () => {
//         try {
//           setLoading(true);
//           const data = await api.registration.getById(id);
//           setRegistration(data);

//           // UPDATED FOR NEW STRUCTURE
//           setFormData({
//             name: data.name || "",
//             batch: data.batch || "",
//             mobile: data.mobile || "",
//             email: data.email || "",
//             tshirtSize: data.tshirtSize || "",
//             accompany: {
//               spouse: data.accompany?.spouse || 0,
//               guest: data.accompany?.guest || 0,
//               driver: data.accompany?.driver || 0,
//               childAbove5: data.accompany?.childAbove5 || 0,
//               childBelow5: data.accompany?.childBelow5 || 0,
//             },
//             childAbove5TshirtSize: data.childAbove5TshirtSize || "",
//             childBelow5TshirtSize: data.childBelow5TshirtSize || "",
//           });

//           if (data.gender) {
//             setGender(data.gender || "Male");
//           }
//         } catch (error) {
//           console.error("❌ Error fetching registration:", error);
//           alert("Failed to load registration data.");
//           navigate("/auth/admin");
//         } finally {
//           setLoading(false);
//         }
//       };

//       fetchRegistrationById();
//     }
//   }, [isEditMode, id, navigate]);

//   useEffect(() => {
//     if (!isEditMode && sharedFormData) {
//       setFormData((prev) => ({
//         ...prev,
//         name: sharedFormData.name || "",
//         batch: sharedFormData.batch || "",
//         mobile: sharedFormData.mobile || "",
//         email: sharedFormData.email || "",
//       }));
//     }
//   }, [sharedFormData, isEditMode]);

//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             spouseFee: bp.spouseFee || 0,
//             guestFee: bp.guestFee || 0,
//             driverFee: bp.driverFee || 0,
//             above5Fee: bp.above5Fee || 0,
//             below5Fee: bp.below5Fee || 0,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   useEffect(() => {
//     if (!gender) return;

//     const fetchTShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender(gender);
//         const tShirtSizes = data.map((item) => item.name);
//         setTShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error("Error fetching t-shirt:", err);
//       }
//     };

//     fetchTShirtSizes();
//   }, [gender]);

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   const calculateTotal = () => {
//     const batch = batchPricing[formData.batch];
//     if (!batch) return 0;

//     return (
//       batch.participantFee +
//       formData.accompany.spouse * (batch.spouseFee || 0) +
//       formData.accompany.guest * (batch.guestFee || 0) +
//       formData.accompany.driver * (batch.driverFee || 0) +
//       formData.accompany.childAbove5 * (batch.above5Fee || 0) +
//       formData.accompany.childBelow5 * (batch.below5Fee || 0)
//     );
//   };

//   const handleInputChange = (field, value) => {
//     setFormData((prev) => ({ ...prev, [field]: value }));

//     if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
//   };

//   const handleAccompanyChange = (type, inc) => {
//     const newCount = Math.max(0, formData.accompany[type] + inc);

//     setFormData((prev) => {
//       // Only child categories have t-shirt size options
//       let sizeField = "";
//       if (type === "childAbove5") {
//         sizeField = "childAbove5TshirtSize";
//       } else if (type === "childBelow5") {
//         sizeField = "childBelow5TshirtSize";
//       }

//       if (sizeField) {
//         const currentSizes = stringToArray(prev[sizeField]);
//         let updatedSizes = currentSizes;

//         if (newCount < prev.accompany[type]) {
//           updatedSizes = currentSizes.slice(0, newCount);
//         } else if (newCount === 0) {
//           updatedSizes = [];
//         }

//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//           [sizeField]: arrayToString(updatedSizes),
//         };
//       } else {
//         // For spouse, guest, driver - no t-shirt size selection needed
//         return {
//           ...prev,
//           accompany: {
//             ...prev.accompany,
//             [type]: newCount,
//           },
//         };
//       }
//     });
//   };

//   const handleTShirtSizeModalSelect = (type, size) => {
//     const currentSizes = stringToArray(formData[type]);
//     const maxSelections =
//       formData.accompany[
//         type === "childAbove5TshirtSize" ? "childAbove5" : "childBelow5"
//       ];

//     if (currentSizes.includes(size)) {
//       const newSizes = currentSizes.filter((s) => s !== size);
//       setFormData((prev) => ({
//         ...prev,
//         [type]: arrayToString(newSizes),
//       }));
//     } else {
//       if (currentSizes.length < maxSelections) {
//         const newSizes = [...currentSizes, size];
//         setFormData((prev) => ({
//           ...prev,
//           [type]: arrayToString(newSizes),
//         }));
//       }
//     }
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     if (!formData.name.trim()) newErrors.name = "Name is required";
//     if (!formData.mobile.trim()) newErrors.mobile = "Mobile required";
//     else if (!/^\d{11}$/.test(formData.mobile))
//       newErrors.mobile = "Must be 11 digits";

//     // Validate child above 5 t-shirt sizes
//     const childAbove5Sizes = stringToArray(formData.childAbove5TshirtSize);
//     if (
//       formData.accompany.childAbove5 > 0 &&
//       childAbove5Sizes.length !== formData.accompany.childAbove5
//     ) {
//       newErrors.childAbove5TshirtSize = `Please select ${formData.accompany.childAbove5} t-shirt size(s) for ${formData.accompany.childAbove5} child(ren) above 5`;
//     }

//     // Validate child below 5 t-shirt sizes
//     const childBelow5Sizes = stringToArray(formData.childBelow5TshirtSize);
//     if (
//       formData.accompany.childBelow5 > 0 &&
//       childBelow5Sizes.length !== formData.accompany.childBelow5
//     ) {
//       newErrors.childBelow5TshirtSize = `Please select ${formData.accompany.childBelow5} t-shirt size(s) for ${formData.accompany.childBelow5} child(ren) below 5`;
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) return false;

//     setLoading(true);
//     try {
//       const formDataToSend = new FormData();
//       formDataToSend.append("name", formData.name);
//       formDataToSend.append("batch", formData.batch);
//       formDataToSend.append("mobile", formData.mobile);
//       formDataToSend.append("email", formData.email);
//       formDataToSend.append("tshirtSize", formData.tshirtSize);
//       formDataToSend.append("gender", gender);
//       formDataToSend.append("accompanySpouse", formData.accompany.spouse);
//       formDataToSend.append("accompanyGuest", formData.accompany.guest);
//       formDataToSend.append("accompanyDriver", formData.accompany.driver);
//       formDataToSend.append(
//         "accompanyChildAbove5",
//         formData.accompany.childAbove5
//       );
//       formDataToSend.append(
//         "accompanyChildBelow5",
//         formData.accompany.childBelow5
//       );
//       formDataToSend.append(
//         "childAbove5TshirtSize",
//         formData.childAbove5TshirtSize || ""
//       );
//       formDataToSend.append(
//         "childBelow5TshirtSize",
//         formData.childBelow5TshirtSize || ""
//       );

//       if (user?.id) {
//         formDataToSend.append("userId", user.id.toString());
//       }

//       let data;
//       if (isEditMode && id) {
//         // Update existing registration
//         data = await api.registration.update(id, formDataToSend);
//       } else {
//         // Create new registration
//         data = await api.registration.create(formDataToSend);
//       }

//       // Reset form for create mode
//       if (!isEditMode) {
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: "",
//           email: "",
//           tshirtSize: "",
//           accompany: {
//             spouse: 0,
//             guest: 0,
//             driver: 0,
//             childAbove5: 0,
//             childBelow5: 0,
//           },
//           childAbove5TshirtSize: "",
//           childBelow5TshirtSize: "",
//         });
//         setGender("Male");
//       }

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);

//       // For edit mode, navigate back to admin after successful update
//       if (isEditMode) {
//         setTimeout(() => navigate("/auth/admin"), 1500);
//       }

//       return data;
//     } catch (error) {
//       console.error("Error submitting registration:", error);
//       alert(
//         `Registration ${isEditMode ? "update" : "failed"}: ${error.message}`
//       );
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePayment = async (participantData) => {
//     if (!validateForm()) return;

//     try {
//       const payload = {
//         name: formData.name,
//         batch: formData.batch,
//         mobile: formData.mobile,
//         email: formData.email,
//         tshirtSize: formData.tshirtSize,
//         accompanySpouse: formData.accompany.spouse,
//         accompanyGuest: formData.accompany.guest,
//         accompanyDriver: formData.accompany.driver,
//         accompanyChildAbove5: formData.accompany.childAbove5,
//         accompanyChildBelow5: formData.accompany.childBelow5,
//         totalAmount: calculateTotal(),
//         order_id: participantData?.orderId || `order_${Date.now()}`,
//         participantId: participantData?.id || id,
//         userId: user?.id,
//         childAbove5TshirtSize: formData.childAbove5TshirtSize,
//         childBelow5TshirtSize: formData.childBelow5TshirtSize,
//       };

//       const data = await api.payment.create(payload);

//       if (data.checkout_url) {
//         window.location.href = data.checkout_url;
//       } else {
//         alert(data.error || "Failed to get checkout URL");
//       }
//     } catch (error) {
//       console.error("Payment request failed", error);
//       alert("Payment request failed. Try again.");
//     }
//   };

//   const handleSave = async () => {
//     const submitted = await handleSubmit();
//     if (submitted && !isEditMode) {
//       // Only proceed to payment for new registrations in create mode
//       await handlePayment(submitted);
//     }
//   };

//   const TShirtSizeModal = ({
//     isOpen,
//     onClose,
//     onSelect,
//     selectedSizesStr,
//     title,
//     maxSelections,
//   }) => {
//     if (!isOpen) return null;

//     const selectedSizes = stringToArray(selectedSizesStr);

//     return (
//       <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
//         <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 w-full max-w-md relative">
//           <button
//             onClick={onClose}
//             className="absolute top-4 right-4 text-red-500 hover:text-gray-700 transition-colors"
//             aria-label="Close modal"
//           >
//             <X size={24} />
//           </button>

//           <h3 className="text-xl font-bold text-gray-800 mb-2 pr-8">{title}</h3>
//           <p className="text-gray-600 mb-4">
//             Select {maxSelections} size(s) for {maxSelections} child(ren)
//           </p>

//           <div className="grid grid-cols-2 gap-3 mb-6">
//             {tShirtSize.map((size) => {
//               const isSelected = selectedSizes.includes(size);
//               const isDisabled =
//                 !isSelected && selectedSizes.length >= maxSelections;

//               return (
//                 <button
//                   key={size}
//                   onClick={() => onSelect(size)}
//                   disabled={isDisabled}
//                   className={`p-4 rounded-xl font-medium transition-all ${
//                     isSelected
//                       ? "bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg"
//                       : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300"
//                   } ${
//                     isDisabled
//                       ? "opacity-50 cursor-not-allowed"
//                       : "hover:shadow-md"
//                   }`}
//                 >
//                   {size}
//                   {isSelected && <span className="text-white ml-2">✓</span>}
//                 </button>
//               );
//             })}
//           </div>

//           <div className="flex justify-between items-center mb-6">
//             <div className="text-sm text-gray-600">
//               Selected:{" "}
//               <span className="font-bold">{selectedSizes.length}</span>/
//               {maxSelections}
//             </div>
//             {selectedSizes.length > 0 && (
//               <div className="text-sm text-gray-700">
//                 <span className="font-medium">Sizes:</span>{" "}
//                 {selectedSizes.join(", ")}
//               </div>
//             )}
//           </div>

//           <div className="flex justify-center">
//             <button
//               onClick={onClose}
//               className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition"
//             >
//               Done
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   const renderSizeButton = (type) => {
//     const sizeField =
//       type === "childAbove5"
//         ? "childAbove5TshirtSize"
//         : "childBelow5TshirtSize";
//     const sizesStr = formData[sizeField];
//     const sizes = stringToArray(sizesStr);
//     const count = formData.accompany[type];

//     if (count === 0) return null;

//     return (
//       <div className="ml-3">
//         <button
//           type="button"
//           onClick={() =>
//             type === "childAbove5"
//               ? setShowChildAbove5TShirtModal(true)
//               : setShowChildBelow5TShirtModal(true)
//           }
//           style={{
//             padding: "6px 12px",
//             backgroundColor: "rgba(255,255,255,0.2)",
//             border: "1px solid rgba(255,255,255,0.3)",
//             color: "#fff",
//             borderRadius: "5px",
//             fontSize: "12px",
//             cursor: "pointer",
//             width: "100%",
//             textAlign: "left",
//             opacity:
//               isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
//           }}
//           disabled={
//             isEditMode && registration?.paymentStatus === "Paid"
//           }
//         >
//           {sizes.length === 0 ? (
//             `Select ${count} T-Shirt(s)`
//           ) : (
//             <div>
//               <div>Sizes: {sizes.join(", ")}</div>
//               <div style={{ fontSize: "10px", opacity: 0.8 }}>
//                 {sizes.length} of {count} selected
//               </div>
//             </div>
//           )}
//         </button>
//       </div>
//     );
//   };

//   // Updated accompany categories with proper labels and pricing
//   const accompanyCategories = [
//     { key: "spouse", label: "Spouse", showTshirt: false },
//     { key: "childAbove5", label: "Children Above 5", showTshirt: true },
//     { key: "childBelow5", label: "Children Below 5", showTshirt: true },
//     { key: "guest", label: "Guest", showTshirt: false },
//     { key: "driver", label: "Driver", showTshirt: false },
//   ];

//   if (loading && isEditMode) {
//     return (
//       <div className="form-container px-4 flex items-center justify-center">
//         <div className="text-center">
//           <div className="spinner-border text-primary" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//           <p className="mt-3 text-white">Loading registration data...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="form-container px-4">
//       {/* Header */}
//       <h2 className="form-title m-0">
//         {isEditMode ? "Edit Registration" : "Event Registration"}
//       </h2>
//       <h5
//         style={{ textAlign: "center", color: "#bdc0ec", marginBottom: "20px" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">
//           🎉 Registration {isEditMode ? "updated" : "submitted"} successfully!
//         </div>
//       )}

//       {/* Payment Status Warning for Edit Mode */}
//       {isEditMode && registration?.paymentStatus === "Paid" && (
//         <div className="alert alert-warning mb-4">
//           <strong>Note:</strong> This registration has already been paid.
//           Editing may affect payment details.
//         </div>
//       )}

//       <form onSubmit={(e) => e.preventDefault()}>
//         {/* Mobile */}
//         <div className="form-group">
//           <label className="form-label">Mobile Number*</label>
//           <input
//             type="tel"
//             className="form-input"
//             placeholder="01XXXXXXXXX (11 digits)"
//             maxLength="11"
//             value={formData.mobile}
//             onChange={(e) => handleInputChange("mobile", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             readOnly={isEditMode && registration?.paymentStatus === "Paid"}
//             style={
//               isEditMode && registration?.paymentStatus === "Paid"
//                 ? { backgroundColor: "#e9ecef", color: "#495057" }
//                 : {}
//             }
//           />
//           {errors.mobile && (
//             <div className="error-message">{errors.mobile}</div>
//           )}
//         </div>

//         {/* Name */}
//         <div className="form-group">
//           <label className="form-label">Name of Participant *</label>
//           <input
//             type="text"
//             className="form-input"
//             placeholder="Enter your full name"
//             value={formData.name}
//             onChange={(e) => handleInputChange("name", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             readOnly={isEditMode && registration?.paymentStatus === "Paid"}
//             style={
//               isEditMode && registration?.paymentStatus === "Paid"
//                 ? { backgroundColor: "#e9ecef", color: "#495057" }
//                 : {}
//             }
//           />
//           {errors.name && <div className="error-message">{errors.name}</div>}
//         </div>

//         {/* Batch */}
//         <div style={{ width: "100%", position: "relative" }}>
//           <label className="form-label">Group</label>
//           <select
//             value={formData.batch}
//             onChange={(e) => handleInputChange("batch", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             style={{
//               width: "100%",
//               padding: "8px 15px",
//               borderRadius: "0px",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               background:
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#e9ecef"
//                   : "rgba(255,255,255,0.1)",
//               color:
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#495057"
//                   : "white",
//               fontSize: "1rem",
//               appearance: "none",
//               WebkitAppearance: "none",
//               MozAppearance: "none",
//               backdropFilter: "blur(8px)",
//               transition: "all 0.3s ease",
//               backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#495057"
//                   : "white"
//               }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//               backgroundRepeat: "no-repeat",
//               backgroundPosition: "right 12px center",
//               backgroundSize: "1rem",
//               marginBottom: "15px",
//             }}
//           >
//             <option
//               value=""
//               style={{ backgroundColor: "#cffae0", color: "black" }}
//             >
//               Select your group
//             </option>
//             {Object.keys(batchPricing).map((batch) => (
//               <option
//                 key={batch}
//                 value={batch}
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 {batch}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Email */}
//         <div className="form-group">
//           <label className="form-label">Email</label>
//           <input
//             type="email"
//             className="form-input"
//             placeholder="your.email@example.com"
//             value={formData.email}
//             onChange={(e) => handleInputChange("email", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             readOnly={isEditMode && registration?.paymentStatus === "Paid"}
//             style={
//               isEditMode && registration?.paymentStatus === "Paid"
//                 ? { backgroundColor: "#e9ecef", color: "#495057" }
//                 : {}
//             }
//           />
//           {errors.email && <div className="error-message">{errors.email}</div>}
//         </div>

//         {/* Gender Selection */}
//         <div className="form-group">
//           <label className="form-label">Select Gender</label>
//           <div
//             style={{
//               display: "flex",
//               gap: "20px",
//               color: "white",
//               marginBottom: "15px",
//             }}
//           >
//             <label
//               style={{ display: "flex", alignItems: "center", gap: "5px" }}
//             >
//               <input
//                 type="radio"
//                 name="gender"
//                 value="Male"
//                 checked={gender === "Male"}
//                 onChange={(e) => setGender(e.target.value)}
//                 disabled={isEditMode && registration?.paymentStatus === "Paid"}
//               />
//               Male
//             </label>
//             <label
//               style={{ display: "flex", alignItems: "center", gap: "5px" }}
//             >
//               <input
//                 type="radio"
//                 name="gender"
//                 value="Female"
//                 checked={gender === "Female"}
//                 onChange={(e) => setGender(e.target.value)}
//                 disabled={isEditMode && registration?.paymentStatus === "Paid"}
//               />
//               Female
//             </label>
//           </div>
//         </div>

//         {/* T-shirt Size */}
//         <div style={{ width: "100%", position: "relative" }}>
//           <label className="form-label">T-Shirt Size</label>
//           <select
//             value={formData.tshirtSize}
//             onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             style={{
//               width: "100%",
//               padding: "8px 15px",
//               borderRadius: "0px",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               background:
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#e9ecef"
//                   : "rgba(255,255,255,0.1)",
//               color:
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#495057"
//                   : "white",
//               fontSize: "1rem",
//               appearance: "none",
//               WebkitAppearance: "none",
//               MozAppearance: "none",
//               backdropFilter: "blur(8px)",
//               transition: "all 0.3s ease",
//               backgroundImage: `url("data:image/svg+xml,%3Csvg fill='${
//                 isEditMode && registration?.paymentStatus === "Paid"
//                   ? "#495057"
//                   : "white"
//               }' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//               backgroundRepeat: "no-repeat",
//               backgroundPosition: "right 12px center",
//               backgroundSize: "1rem",
//               marginBottom: "15px",
//             }}
//           >
//             <option
//               value=""
//               style={{ backgroundColor: "#cffae0", color: "black" }}
//             >
//               Select your T-Shirt Size
//             </option>
//             {tShirtSize.map((size) => (
//               <option
//                 key={size}
//                 value={size}
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 {size}
//               </option>
//             ))}
//           </select>
//           {errors.tshirtSize && (
//             <div className="error-message">{errors.tshirtSize}</div>
//           )}
//         </div>

//         {/* Accompany Section - UPDATED */}
//         <div className="form-group">
//           <label className="form-label">Accompanying Persons</label>
//           <div
//             className="accompany-section"
//             style={{
//               padding: "15px",
//               borderRadius: "12px",
//               background: "rgba(255,255,255,0.2)",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               opacity:
//                 isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
//             }}
//           >
//             {accompanyCategories.map((category) => (
//               <div key={category.key}>
//                 <div
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "space-between",
//                     marginBottom: "15px",
//                     color: "#fff",
//                   }}
//                 >
//                   <span style={{ flex: 1 }}>
//                     {category.label} (
//                     {(() => {
//                       const amount =
//                         batchPricing[formData.batch]?.[
//                           category.key === "spouse"
//                             ? "spouseFee"
//                             : category.key === "guest"
//                             ? "guestFee"
//                             : category.key === "driver"
//                             ? "driverFee"
//                             : category.key === "childAbove5"
//                             ? "above5Fee"
//                             : "below5Fee"
//                         ] || 0;

//                       return amount > 0 ? `Tk. ${amount}` : "Free";
//                     })()}
//                     )
//                   </span>

//                   <div
//                     style={{
//                       display: "flex",
//                       alignItems: "center",
//                       gap: "10px",
//                     }}
//                   >
//                     <button
//                       className="btn"
//                       style={{
//                         backgroundColor: "rgba(255,255,255,0.2)",
//                         border: "1px solid rgba(255,255,255,0.3)",
//                         color: "#fff",
//                         width: "30px",
//                         height: "30px",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                       }}
//                       type="button"
//                       onClick={() => handleAccompanyChange(category.key, -1)}
//                       disabled={
//                         isEditMode && registration?.paymentStatus === "Paid"
//                       }
//                     >
//                       -
//                     </button>
//                     <span style={{ width: "40px", textAlign: "center" }}>
//                       {formData.accompany[category.key]}
//                     </span>
//                     <button
//                       className="btn"
//                       style={{
//                         backgroundColor: "rgba(255,255,255,0.2)",
//                         border: "1px solid rgba(255,255,255,0.3)",
//                         color: "#fff",
//                         width: "30px",
//                         height: "30px",
//                         display: "flex",
//                         alignItems: "center",
//                         justifyContent: "center",
//                       }}
//                       type="button"
//                       onClick={() => handleAccompanyChange(category.key, 1)}
//                       disabled={
//                         isEditMode && registration?.paymentStatus === "Paid"
//                       }
//                     >
//                       +
//                     </button>

//                     {/* Only show T-shirt size button for children categories */}
//                     {category.showTshirt && renderSizeButton(category.key)}
//                   </div>
//                 </div>

//                 {/* Display validation errors for children T-shirt sizes */}
//                 {formData.accompany[category.key] > 0 &&
//                   category.showTshirt && (
//                     <div style={{ marginBottom: "10px" }}>
//                       {category.key === "childAbove5" &&
//                         errors.childAbove5TshirtSize && (
//                           <div
//                             className="error-message"
//                             style={{
//                               color: "#ff6b6b",
//                               marginTop: "5px",
//                               fontSize: "12px",
//                             }}
//                           >
//                             {errors.childAbove5TshirtSize}
//                           </div>
//                         )}
//                       {category.key === "childBelow5" &&
//                         errors.childBelow5TshirtSize && (
//                           <div
//                             className="error-message"
//                             style={{
//                               color: "#ff6b6b",
//                               marginTop: "5px",
//                               fontSize: "12px",
//                             }}
//                           >
//                             {errors.childBelow5TshirtSize}
//                           </div>
//                         )}
//                     </div>
//                   )}
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* T-Shirt Size Modals */}
//         <TShirtSizeModal
//           isOpen={showChildAbove5TShirtModal}
//           onClose={() => setShowChildAbove5TShirtModal(false)}
//           onSelect={(size) =>
//             handleTShirtSizeModalSelect("childAbove5TshirtSize", size)
//           }
//           selectedSizesStr={formData.childAbove5TshirtSize}
//           title="Select T-Shirt Sizes for Children Above 5"
//           maxSelections={formData.accompany.childAbove5}
//         />

//         <TShirtSizeModal
//           isOpen={showChildBelow5TShirtModal}
//           onClose={() => setShowChildBelow5TShirtModal(false)}
//           onSelect={(size) =>
//             handleTShirtSizeModalSelect("childBelow5TshirtSize", size)
//           }
//           selectedSizesStr={formData.childBelow5TshirtSize}
//           title="Select T-Shirt Sizes for Children Below 5"
//           maxSelections={formData.accompany.childBelow5}
//         />

//         {/* Total */}
//         <div
//           className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             fontSize: "1.5rem",
//             marginTop: "20px",
//           }}
//         >
//           <span>Total amount:</span>
//           <span className="amount-title">৳{calculateTotal()}</span>
//         </div>

//         {/* Buttons */}
//         <div className="button-group flex gap-4 mt-6">
//           {!isEditMode ? (
//             // For new registration
//             <button
//               type="button"
//               className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//               onClick={handleSave}
//               disabled={loading || (isEditMode && registration?.paymentStatus === "Success")}
//             >
//               {loading ? (
//                 <>
//                   <div
//                     className="spinner-border spinner-border-sm"
//                     role="status"
//                   >
//                     <span className="visually-hidden">Loading...</span>
//                   </div>
//                   Processing...
//                 </>
//               ) : (
//                 "Submit & Pay Now"
//               )}
//             </button>
//           ) : (
//             // For edit mode - two buttons
//             <>
//               <button
//                 type="button"
//                 className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white text-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//                 onClick={handleSave}
//                 disabled={loading || registration?.paymentStatus === "Success"}
//               >
//                 {loading ? (
//                   <>
//                     <div
//                       className="spinner-border spinner-border-sm"
//                       role="status"
//                     >
//                       <span className="visually-hidden">Loading...</span>
//                     </div>
//                     Processing...
//                   </>
//                 ) : registration?.paymentStatus === "Success" ? (
//                   "Already Paid"
//                 ) : (
//                   "Update & Pay Now"
//                 )}
//               </button>

//               <button
//                 type="button"
//                 className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-blue-600 text-white rounded-xl transition"
//                 onClick={handleSubmit}
//                 disabled={loading}
//               >
//                 {loading ? (
//                   <>
//                     <div
//                       className="spinner-border spinner-border-sm"
//                       role="status"
//                     >
//                       <span className="visually-hidden">Loading...</span>
//                     </div>
//                     Updating...
//                   </>
//                 ) : (
//                   "Update Changes"
//                 )}
//               </button>
//             </>
//           )}
//         </div>

//         <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={() => navigate(-1)}
//           >
//             <ArrowLeft size={18} />
//             Back
//           </button>

//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Home
//           </button>
//         </div>

//         <div className="d-flex justify-content-center mt-4">
//           <img
//             src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
//             alt="Payment"
//             className="img-fluid h-25 object-fit-cover rounded-2"
//           />
//         </div>

//         <div className="hero-text mt-8 flex justify-center items-center">
//           <motion.p
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ delay: 0.5 }}
//             style={{ fontSize: "16px", color: "#fff" }}
//           >
//             পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
//           </motion.p>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default EditRegistrationPage;



// import { motion } from "framer-motion";
// import { ArrowLeft, Home } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const EditRegistrationPage = ({ sharedFormData, isEditMode = false }) => {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   const [formData, setFormData] = useState({
//     name: "",
//     batch: "",
//     mobile: "",
//     email: "",
//     tshirtSize: "",
//     accompany: { adult: 0, childBelow5: 0 },
//     adultTshirtSize: "",
//     childBelow5TshirtSize: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [batchPricing, setBatchPricing] = useState({});
//   const [tShirtSize, setTShirtSize] = useState([]);
//   const [loading, setLoading] = useState(isEditMode);
//   const [registration, setRegistration] = useState(null);
//   const [gender, setGender] = useState("Male");
//   const [showAdultTShirtModal, setShowAdultTShirtModal] = useState(false);
//   const [showChildTShirtModal, setShowChildTShirtModal] = useState(false);

//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const { user, getAuthHeader } = useAuth();

//   useEffect(() => {
//     if (isEditMode && id) {
//       const fetchRegistrationById = async () => {
//         try {
//           setLoading(true);
//           const data = await api.registration.getById(id);
//           setRegistration(data);

//           setFormData({
//             name: data.name || "",
//             batch: data.batch || "",
//             mobile: data.mobile || "",
//             email: data.email || "",
//             tshirtSize: data.tshirtSize || "",
//             accompany: {
//               adult: data.accompany?.adult || 0,
//               childBelow5: data.accompany?.childBelow5 || 0,
//             },
//             adultTshirtSize: data.adultTshirtSize || "",
//             childBelow5TshirtSize: data.childBelow5TshirtSize || "",
//           });

//           if (data.tshirtSize) {
//             setGender(data.gender || "Male");
//           }
//         } catch (error) {
//           console.error("❌ Error fetching registration:", error);
//           alert("Failed to load registration data.");
//           navigate("/auth/admin");
//         } finally {
//           setLoading(false);
//         }
//       };

//       fetchRegistrationById();
//     }
//   }, [isEditMode, id, navigate]);

//   useEffect(() => {
//     if (!isEditMode && sharedFormData) {
//       setFormData((prev) => ({
//         ...prev,
//         name: sharedFormData.name || "",
//         batch: sharedFormData.batch || "",
//         mobile: sharedFormData.mobile || "",
//         email: sharedFormData.email || "",
//       }));
//     }
//   }, [sharedFormData, isEditMode]);

//   useEffect(() => {
//     const fetchBatchPricing = async () => {
//       try {
//         const data = await api.batchPricing.getAll();
//         const pricingMap = {};
//         data.forEach((bp) => {
//           pricingMap[bp.batchName] = {
//             participantFee: bp.participantFee,
//             adultFee: bp.adultFee,
//             below5Fee: bp.below5Fee,
//           };
//         });
//         setBatchPricing(pricingMap);
//       } catch (err) {
//         console.error("Error fetching batch pricing:", err);
//       }
//     };

//     fetchBatchPricing();
//   }, []);

//   useEffect(() => {
//     if (!gender) return;

//     const fetchTShirtSizes = async () => {
//       try {
//         const data = await api.lookup.getTShirtByGender(gender);
//         const tShirtSizes = data.map((item) => item.name);
//         setTShirtSize(tShirtSizes);
//       } catch (err) {
//         console.error("Error fetching t-shirt:", err);
//       }
//     };

//     fetchTShirtSizes();
//   }, [gender]);

//   const handleHomeClick = () => {
//     navigate("/home");
//   };

//   const calculateTotal = () => {
//     const batch = batchPricing[formData.batch];
//     if (!batch) return 0;

//     return (
//       batch.participantFee +
//       formData.accompany.adult * batch.adultFee +
//       formData.accompany.childBelow5 * batch.below5Fee
//     );
//   };

//   const handleInputChange = (field, value) => {
//     if (field === "batch") {
//       const selectedBatch = batchPricing[value];
//       setFormData((prev) => ({
//         ...prev,
//         batch: value,
//         accompany: {
//           adult: 0,
//           childBelow5: 0,
//         },
//         adultTshirtSize: "",
//         childBelow5TshirtSize: "",
//       }));
//     } else {
//       setFormData((prev) => ({ ...prev, [field]: value }));
//     }

//     if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
//   };

//   const handleAccompanyChange = (type, inc) => {
//     const newCount = Math.max(0, formData.accompany[type] + inc);
//     setFormData((prev) => ({
//       ...prev,
//       accompany: {
//         ...prev.accompany,
//         [type]: newCount,
//       },
//       ...(type === "adult" && newCount === 0 && { adultTshirtSize: "" }),
//       ...(type === "childBelow5" &&
//         newCount === 0 && { childBelow5TshirtSize: "" }),
//     }));
//   };

//   const handleTShirtSizeChange = (type, size) => {
//     setFormData((prev) => ({
//       ...prev,
//       [type]: size,
//     }));

//     if (type === "adultTshirtSize") {
//       setShowAdultTShirtModal(false);
//     } else {
//       setShowChildTShirtModal(false);
//     }
//   };

//   const validateForm = () => {
//     const newErrors = {};
//     if (!formData.name.trim()) newErrors.name = "Name is required";
//     if (!formData.batch) newErrors.batch = "Batch selection required";
//     if (!formData.mobile.trim()) newErrors.mobile = "Mobile required";
//     else if (!/^\d{11}$/.test(formData.mobile))
//       newErrors.mobile = "Must be 11 digits";
//     if (!formData.email.trim()) newErrors.email = "Email required";
//     if (!formData.tshirtSize) newErrors.tshirtSize = "T-shirt size required";
//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) return false;

//     setLoading(true);
//     try {
//       const formDataToSend = new FormData();
//       formDataToSend.append("name", formData.name);
//       formDataToSend.append("batch", formData.batch);
//       formDataToSend.append("mobile", formData.mobile);
//       formDataToSend.append("email", formData.email);
//       formDataToSend.append("tshirtSize", formData.tshirtSize);
//       formDataToSend.append("accompanyAdult", formData.accompany.adult);
//       formDataToSend.append(
//         "accompanyChildBelow5",
//         formData.accompany.childBelow5
//       );
//       formDataToSend.append("adultTshirtSize", formData.adultTshirtSize);
//       formDataToSend.append(
//         "childBelow5TshirtSize",
//         formData.childBelow5TshirtSize
//       );

//       if (user?.id) {
//         formDataToSend.append("userId", user.id.toString());
//       }

//       let data;
//       if (isEditMode && id) {
//         // Update existing registration
//         data = await api.registration.update(id, formDataToSend);
//       } else {
//         // Create new registration
//         data = await api.registration.create(formDataToSend);
//       }

//       // Reset form for create mode
//       if (!isEditMode) {
//         setFormData({
//           name: "",
//           batch: "",
//           mobile: "",
//           email: "",
//           tshirtSize: "",
//           accompany: { adult: 0, childBelow5: 0 },
//           adultTshirtSize: "",
//           childBelow5TshirtSize: "",
//         });
//       }

//       setShowSuccess(true);
//       setTimeout(() => setShowSuccess(false), 3000);

//       // For edit mode, navigate back to home after successful update
//       if (isEditMode) {
//         setTimeout(() => navigate("/auth/admin"), 1500);
//       }

//       return data;
//     } catch (error) {
//       console.error("Error submitting registration:", error);
//       alert(
//         `Registration ${isEditMode ? "update" : "failed"}: ${error.message}`
//       );
//       return false;
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handlePayment = async (participantData) => {
//     if (!validateForm()) return;

//     try {
//       const payload = {
//         name: formData.name,
//         batch: formData.batch,
//         mobile: formData.mobile,
//         email: formData.email,
//         tshirtSize: formData.tshirtSize,
//         accompanyAdult: formData.accompany.adult,
//         accompanyChildBelow5: formData.accompany.childBelow5,
//         totalAmount: calculateTotal(),
//         order_id: participantData?.orderId || `order_${Date.now()}`,
//         participantId: participantData?.id || id,
//         userId: user?.id,
//         adultTshirtSize: formData.adultTshirtSize,
//         childBelow5TshirtSize: formData.childBelow5TshirtSize,
//       };

//       const data = await api.payment.create(payload);

//       if (data.checkout_url) {
//         window.location.href = data.checkout_url;
//       } else {
//         alert(data.error || "Failed to get checkout URL");
//       }
//     } catch (error) {
//       console.error("Payment request failed", error);
//       alert("Payment request failed. Try again.");
//     }
//   };

//   const handleSave = async () => {
//     const submitted = await handleSubmit();
//     if (submitted && isEditMode) {
//       // Only proceed to payment for new registrations
//       await handlePayment(submitted);
//     }
//   };

//   const accompanyLabels = {
//     adult: "Adult",
//     childBelow5: "Children Below 5",
//   };

//   if (loading && isEditMode) {
//     return (
//       <div className="form-container px-4 flex items-center justify-center">
//         <div className="text-center">
//           <div className="spinner-border text-primary" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//           <p className="mt-3 text-white">Loading registration data...</p>
//         </div>
//       </div>
//     );
//   }

//   const TShirtSizeModal = ({
//     isOpen,
//     onClose,
//     onSelect,
//     selectedSize,
//     title,
//   }) => {
//     if (!isOpen) return null;

//     return (
//       <div
//         style={{
//           position: "fixed",
//           top: 0,
//           left: 0,
//           right: 0,
//           bottom: 0,
//           backgroundColor: "rgba(0,0,0,0.7)",
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           zIndex: 1000,
//         }}
//       >
//         <div
//           style={{
//             backgroundColor: "#4b2882",
//             padding: "20px",
//             borderRadius: "10px",
//             width: "90%",
//             maxWidth: "400px",
//             border: "1px solid rgba(255,255,255,0.2)",
//           }}
//         >
//           <h3
//             style={{
//               color: "white",
//               marginBottom: "20px",
//               textAlign: "center",
//             }}
//           >
//             {title}
//           </h3>
//           <div
//             style={{
//               display: "grid",
//               gridTemplateColumns: "repeat(2, 1fr)",
//               gap: "10px",
//               marginBottom: "20px",
//             }}
//           >
//             {tShirtSize.map((size) => (
//               <button
//                 key={size}
//                 onClick={() => onSelect(size)}
//                 style={{
//                   padding: "10px",
//                   backgroundColor:
//                     selectedSize === size ? "#6b46c1" : "rgba(255,255,255,0.1)",
//                   border: `1px solid ${
//                     selectedSize === size ? "#9f7aea" : "rgba(255,255,255,0.2)"
//                   }`,
//                   color: "white",
//                   borderRadius: "5px",
//                   cursor: "pointer",
//                   transition: "all 0.3s",
//                 }}
//               >
//                 {size}
//               </button>
//             ))}
//           </div>
//           <div style={{ display: "flex", justifyContent: "center" }}>
//             <button
//               onClick={onClose}
//               style={{
//                 padding: "10px 20px",
//                 backgroundColor: "rgba(255,255,255,0.2)",
//                 border: "1px solid rgba(255,255,255,0.3)",
//                 color: "white",
//                 borderRadius: "5px",
//                 cursor: "pointer",
//               }}
//             >
//               Close
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="form-container px-4">
//       {/* Header */}
//       <h2 className="form-title m-0">
//         {isEditMode ? "Edit Registration" : "Event Registration"}
//       </h2>
//       <h5
//         style={{ textAlign: "center", color: "#bdc0ec", marginBottom: "20px" }}
//       >
//         KGC'97 Day 2026
//       </h5>

//       {showSuccess && (
//         <div className="success-message">
//           🎉 Registration {isEditMode ? "updated" : "submitted"} successfully!
//         </div>
//       )}

//       {/* Payment Status Warning for Edit Mode */}
//       {isEditMode && registration?.paymentStatus === "Paid" && (
//         <div className="alert alert-warning mb-4">
//           <strong>Note:</strong> This registration has already been paid.
//           Editing may affect payment details.
//         </div>
//       )}

//       <form onSubmit={(e) => e.preventDefault()}>
//         {/* Mobile */}
//         <div className="form-group">
//           <label className="form-label">Mobile Number*</label>
//           <input
//             type="tel"
//             className="form-input"
//             placeholder="01XXXXXXXXX (11 digits)"
//             maxLength="11"
//             value={formData.mobile}
//             onChange={(e) => handleInputChange("mobile", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//           />
//           {errors.mobile && (
//             <div className="error-message">{errors.mobile}</div>
//           )}
//         </div>

//         {/* Name */}
//         <div className="form-group">
//           <label className="form-label">Name of Participant *</label>
//           <input
//             type="text"
//             className="form-input"
//             placeholder="Enter your full name"
//             value={formData.name}
//             onChange={(e) => handleInputChange("name", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//           />
//           {errors.name && <div className="error-message">{errors.name}</div>}
//         </div>

//         {/* Batch */}
//         <div style={{ width: "100%", position: "relative" }}>
//           <label className="form-label">Group *</label>
//           <select
//             value={formData.batch}
//             onChange={(e) => handleInputChange("batch", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             style={{
//               width: "100%",
//               padding: "8px 15px",
//               borderRadius: "0px",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               background: "rgba(255,255,255,0.1)",
//               color: "white",
//               fontSize: "1rem",
//               appearance: "none",
//               WebkitAppearance: "none",
//               MozAppearance: "none",
//               backdropFilter: "blur(8px)",
//               transition: "all 0.3s ease",
//               backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//               backgroundRepeat: "no-repeat",
//               backgroundPosition: "right 12px center",
//               backgroundSize: "1rem",
//               marginBottom: "15px",
//               opacity:
//                 isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
//             }}
//           >
//             <option
//               value=""
//               style={{ backgroundColor: "#cffae0", color: "black" }}
//             >
//               Select your group
//             </option>
//             {Object.keys(batchPricing).map((batch) => (
//               <option
//                 key={batch}
//                 value={batch}
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 {batch}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* Email */}
//         <div className="form-group">
//           <label className="form-label">Email *</label>
//           <input
//             type="email"
//             className="form-input"
//             placeholder="your.email@example.com"
//             value={formData.email}
//             onChange={(e) => handleInputChange("email", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//           />
//           {errors.email && <div className="error-message">{errors.email}</div>}
//         </div>

//         {/* Gender Selection */}
//         <div className="form-group">
//           <label className="form-label">Select Gender *</label>
//           <div
//             style={{
//               display: "flex",
//               gap: "20px",
//               color: "white",
//               marginBottom: "15px",
//             }}
//           >
//             <label
//               style={{ display: "flex", alignItems: "center", gap: "5px" }}
//             >
//               <input
//                 type="radio"
//                 name="gender"
//                 value="Male"
//                 checked={gender === "Male"}
//                 onChange={(e) => setGender(e.target.value)}
//                 disabled={isEditMode && registration?.paymentStatus === "Paid"}
//               />
//               Male
//             </label>
//             <label
//               style={{ display: "flex", alignItems: "center", gap: "5px" }}
//             >
//               <input
//                 type="radio"
//                 name="gender"
//                 value="Female"
//                 checked={gender === "Female"}
//                 onChange={(e) => setGender(e.target.value)}
//                 disabled={isEditMode && registration?.paymentStatus === "Paid"}
//               />
//               Female
//             </label>
//           </div>
//         </div>

//         {/* T-shirt Size */}
//         <div style={{ width: "100%", position: "relative" }}>
//           <label className="form-label">T-Shirt Size *</label>
//           <select
//             value={formData.tshirtSize}
//             onChange={(e) => handleInputChange("tshirtSize", e.target.value)}
//             disabled={isEditMode && registration?.paymentStatus === "Paid"}
//             style={{
//               width: "100%",
//               padding: "8px 15px",
//               borderRadius: "0px",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               background: "rgba(255,255,255,0.1)",
//               color: "white",
//               fontSize: "1rem",
//               appearance: "none",
//               WebkitAppearance: "none",
//               MozAppearance: "none",
//               backdropFilter: "blur(8px)",
//               transition: "all 0.3s ease",
//               backgroundImage: `url("data:image/svg+xml,%3Csvg fill='white' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.23 7.21a.75.75 0 011.06 0L10 10.92l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 010-1.06z'/%3E%3C/svg%3E")`,
//               backgroundRepeat: "no-repeat",
//               backgroundPosition: "right 12px center",
//               backgroundSize: "1rem",
//               marginBottom: "15px",
//               opacity:
//                 isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
//             }}
//           >
//             <option
//               value=""
//               style={{ backgroundColor: "#cffae0", color: "black" }}
//             >
//               Select your T-Shirt Size
//             </option>
//             {tShirtSize.map((size) => (
//               <option
//                 key={size}
//                 value={size}
//                 style={{ backgroundColor: "#cffae0", color: "black" }}
//               >
//                 {size}
//               </option>
//             ))}
//           </select>
//         </div>

//         <div className="form-group">
//           <label className="form-label">Accompanying Persons</label>
//           <div
//             className="accompany-section"
//             style={{
//               padding: "15px",
//               borderRadius: "12px",
//               background: "rgba(255,255,255,0.2)",
//               border: "1px solid rgba(255, 255, 255, 0.2)",
//               opacity:
//                 isEditMode && registration?.paymentStatus === "Paid" ? 0.6 : 1,
//             }}
//           >
//             {["adult", "childBelow5"].map((type) => (
//               <div key={type}>
//                 <div
//                   style={{
//                     display: "flex",
//                     alignItems: "center",
//                     justifyContent: "space-between",
//                     marginBottom: "10px",
//                     color: "#fff",
//                   }}
//                 >
//                   <span style={{ flex: 1 }}>
//                     {accompanyLabels[type]} (৳
//                     {batchPricing[formData.batch]?.[
//                       type === "adult" ? "adultFee" : "below5Fee"
//                     ] || 0}{" "}
//                     each )
//                   </span>
//                   <button
//                     className="btn"
//                     style={{
//                       backgroundColor: "rgba(255,255,255,0.2)",
//                       border: "1px solid rgba(255,255,255,0.3)",
//                       color: "#fff",
//                       width: "30px",
//                       height: "30px",
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                     type="button"
//                     onClick={() => handleAccompanyChange(type, -1)}
//                     disabled={
//                       isEditMode && registration?.paymentStatus === "Paid"
//                     }
//                   >
//                     -
//                   </button>
//                   <span style={{ width: "40px", textAlign: "center" }}>
//                     {formData.accompany[type]}
//                   </span>
//                   <button
//                     className="btn"
//                     style={{
//                       backgroundColor: "rgba(255,255,255,0.2)",
//                       border: "1px solid rgba(255,255,255,0.3)",
//                       color: "#fff",
//                       width: "30px",
//                       height: "30px",
//                       display: "flex",
//                       alignItems: "center",
//                       justifyContent: "center",
//                     }}
//                     type="button"
//                     onClick={() => handleAccompanyChange(type, 1)}
//                     disabled={
//                       isEditMode && registration?.paymentStatus === "Paid"
//                     }
//                   >
//                     +
//                   </button>

//                   {/* T-Shirt Size Button */}
//                   {formData.accompany[type] > 0 && (
//                     <button
//                       type="button"
//                       onClick={() =>
//                         type === "adult"
//                           ? setShowAdultTShirtModal(true)
//                           : setShowChildTShirtModal(true)
//                       }
//                       style={{
//                         marginLeft: "10px",
//                         padding: "5px 10px",
//                         backgroundColor: "rgba(255,255,255,0.2)",
//                         border: "1px solid rgba(255,255,255,0.3)",
//                         color: "#fff",
//                         borderRadius: "5px",
//                         fontSize: "12px",
//                         cursor: "pointer",
//                         minWidth: "100px",
//                       }}
//                       disabled={
//                         isEditMode && registration?.paymentStatus === "Paid"
//                       }
//                     >
//                       {type === "adult"
//                         ? formData.adultTshirtSize
//                           ? `Size: ${formData.adultTshirtSize}`
//                           : "Select T-Shirt"
//                         : formData.childBelow5TshirtSize
//                         ? `Size: ${formData.childBelow5TshirtSize}`
//                         : "Select T-Shirt"}
//                     </button>
//                   )}
//                 </div>

//                 {/* Error message for t-shirt size */}
//                 {formData.accompany[type] > 0 && (
//                   <div style={{ marginBottom: "10px", fontSize: "12px" }}>
//                     {type === "adult" && errors.adultTshirtSize && (
//                       <div
//                         className="error-message"
//                         style={{ color: "#ff6b6b", marginTop: "5px" }}
//                       >
//                         {errors.adultTshirtSize}
//                       </div>
//                     )}
//                     {type === "childBelow5" && errors.childBelow5TshirtSize && (
//                       <div
//                         className="error-message"
//                         style={{ color: "#ff6b6b", marginTop: "5px" }}
//                       >
//                         {errors.childBelow5TshirtSize}
//                       </div>
//                     )}
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>
//         {/* T-Shirt Size Modals */}
//         <TShirtSizeModal
//           isOpen={showAdultTShirtModal}
//           onClose={() => setShowAdultTShirtModal(false)}
//           onSelect={(size) => handleTShirtSizeChange("adultTshirtSize", size)}
//           selectedSize={formData.adultTshirtSize}
//           title="Select Adult T-Shirt Size"
//         />

//         <TShirtSizeModal
//           isOpen={showChildTShirtModal}
//           onClose={() => setShowChildTShirtModal(false)}
//           onSelect={(size) =>
//             handleTShirtSizeChange("childBelow5TshirtSize", size)
//           }
//           selectedSize={formData.childBelow5TshirtSize}
//           title="Select Child T-Shirt Size"
//         />
//         {/* Total */}
//         <div
//           className="amount-display"
//           style={{
//             display: "flex",
//             justifyContent: "space-between",
//             alignItems: "center",
//             fontSize: "1.5rem",
//           }}
//         >
//           <span>Total amount:</span>
//           <span className="amount-title">৳{calculateTotal()}</span>
//         </div>

//         {/* Buttons */}
//         <div className="button-group flex gap-4 mt-6">
//           <button
//             type="button"
//             className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
//             onClick={handleSave}
//             disabled={loading}
//           >
//             {loading ? (
//               <>
//                 <div className="spinner-border spinner-border-sm" role="status">
//                   <span className="visually-hidden">Loading...</span>
//                 </div>
//                 {isEditMode ? "Updating..." : "Processing..."}
//               </>
//             ) : isEditMode ? (
//               "Pay Now & Update"
//             ) : (
//               "Pay Now & Submit"
//             )}
//           </button>

//           {isEditMode && (
//             <button
//               type="button"
//               className="flex-1 md:flex-auto flex justify-center items-center gap-2 px-6 py-2.5 bg-yellow-500 hover:bg-blue-600 text-white rounded-xl transition"
//               onClick={handleSubmit}
//               disabled={loading}
//             >
//               Update Changes
//             </button>
//           )}
//         </div>
//         <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={() => navigate(-1)}
//           >
//             <ArrowLeft size={18} />
//             Back
//           </button>

//           <button
//             type="button"
//             className="flex items-center gap-2 px-4 py-2 text-white rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition"
//             onClick={handleHomeClick}
//           >
//             <Home size={18} />
//             Home
//           </button>
//         </div>

//         <div className="d-flex justify-content-center mt-4">
//           <img
//             src={`${process.env.PUBLIC_URL}/images/payment.jpg`}
//             alt="Payment"
//             className="img-fluid h-25 object-fit-cover rounded-2"
//           />
//         </div>

//         <div className="hero-text mt-8 flex justify-center items-center">
//           <motion.p
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ delay: 0.5 }}
//             style={{ fontSize: "16px", color: "#fff" }}
//           >
//             পেমেন্ট সংক্রান্ত টেকনিক্যাল সমস্যায় যোগাযোগ করুন : 01711065316
//           </motion.p>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default EditRegistrationPage;
